const capitalize = require('capitalize');

const HOUR = 60 * 60 * 1000; // in ms
const DAY = 24 * HOUR; // in ms
const YEAR = 365 * DAY; // in ms
const TRIAL_RESET_INTERVAL = YEAR + 30 * DAY; // 13 months, after which a user is offered a fresh trial

/**
 * @param {string} email 
 * @return {string}
 */
function getDomain(email) {
  return email.split('@').pop().toLowerCase();
}

const OPEN_DATA_API_URL = 'https://public.opendatasoft.com/api/explore/v2.1/catalog/datasets/';
const OPEN_DATA_FIELDS = 'objectid,name,state,city,level,website'; // alias?, type?
const OPEN_DATA_DATASETS = [{
  db_name: 'us-public-schools',
  has_alias: false,
  default_type: 'Lower',
  source: 'https://public.opendatasoft.com/api/explore/v2.1/catalog/datasets/us-public-schools/exports/json?lang=en',
  local_source: 'data/us-public-schools.json'
}, {
  db_name: 'us-private-schools',
  has_alias: false,
  default_type: 'Lower',
  source: 'https://public.opendatasoft.com/api/explore/v2.1/catalog/datasets/us-private-schools/exports/json?lang=en',
  local_source: 'data/us-private-schools.json'
}, {
  db_name: 'us-colleges-and-universities',
  has_alias: true,
  default_type: 'Higher',
  source: 'https://public.opendatasoft.com/api/explore/v2.1/catalog/datasets/us-colleges-and-universities/exports/json?lang=en',
  local_source: 'data/us-colleges-and-universities.json'
}];

const COMMON_DOMAINS = ['gmail.com'];

/**
 * @param {function(*):string} extractor 
 * @param {string|undefined} locales
 * @param {Object|undefined} options
 * @returns {function(*,*):number}
 */
function sorter(extractor, locales, options) {
  return (a, b) => {
    val_a = extractor(a);
    val_b = extractor(b);
    return val_a.localeCompare(val_b, locales, options);
  };
}

/**
 * @param {string} name
 * @return {string} 
 */
function capitalizeName(name) {
  const isAllUpper = name.toUpperCase() == name;
  const isAllLower = name.toLowerCase() == name;
  // All lowercase: always Title-Case. All uppercase: Title-Case unless it
  // looks like an acronym (single word, ≤5 chars). Mixed case: trust the user.
  const looksLikeAcronym = isAllUpper && !/\s/.test(name) && name.length <= 5;
  if ((isAllLower || isAllUpper) && !looksLikeAcronym) {
    return capitalize.words(name)
      .replace(/Mc([a-z])/g, (_, letter) => 'Mc' + letter.toUpperCase());
  }
  return name;
}

// double-quote-like marks (incl. guillemets and language-specific low quotes)
// and backtick — stripped from school/city names. Apostrophes and single
// quotes are preserved for legitimate names like "St. Mary's".
const QUOTE_REGEX = /["`«»‚“”„‟‹›]/g;

/**
 * @param {string} name
 * @return {string} name with quote characters stripped and whitespace
 *                  collapsed/trimmed; safe to use as a lookup key.
 */
function normalizeName(name) {
  return name
    .replace(QUOTE_REGEX, '')
    .replace(/,{2,}/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Reasoning: opendatasoft LIKE matches string w/o account, case insensitive and replaces some [-'.] with space
 * returns normalize city name so that if citied C and D are mapped to the same string, then C LIKE D (in opendatasoft) should return true
 * @param {string} city
 * @return {string} 
 */
function normalizeCity(city) {
  return removeAccents(city).toLowerCase()
    .replace(/-/g, ' ')
    .replace(/['\.] /g, ' ')
    .replace(/^['‘]/, '')
    .replace(/\s+/g, ' ');
}

/**
 * @param {Object} school (update in place)
 * @param {string} source 
 */
function cleanUpSchoolObject(school, source = '') {
  if (source)
    school.source = source;
  if (school.name.endsWith('-' + school.city))
    school.name = school.name.slice(0, -(school.city.length + 1));
  school.name = capitalizeName(school.name);
  school.city = capitalizeName(school.city);
  school.type = ['ADULT EDUCATION', 'PREKINDERGARTEN', 'UNGRADED'].includes(school.level) ?
    'Other' :
    OPEN_DATA_DATASETS.find(db => db.db_name == school.source).default_type;
  if (school.website == 'NOT AVAILABLE')
    delete school.website;
  delete school.level;
}

/**
 * @param {string} s 
 * @return {string}
 */
function removeAccents(s) {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * @param {string} s 
 * @return {number}
 */
function wordToPercentage(s) {
  s = removeAccents(s).toUpperCase();
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const size = alphabet.length;
  // enough to consider the first 2 letters:
  let percentage = 0;
  let multiplier = 1 / size;
  for (let i = 0; i < 2; i++) {
    const index = Math.max(0, alphabet.indexOf(s[i]));
    percentage += index * multiplier;
    multiplier /= size;
  }
  return Math.round(100 * percentage);
}

function withTransaction(knex, trx, fn) {
  if (trx) {
    return fn(trx);
  } else {
    return knex.transaction(fn);
  }
}

function capitalizeFirstLetter(str) {
  return str[0].toUpperCase() + str.slice(1);
}

function sourceToLetter(source) {
  switch (source) {
    case 'us-public-schools': return 'a';
    case 'us-private-schools': return 'b';
    case 'us-colleges-and-universities': return 'c';
    default:
      throw new Error('Unknown source: ' + source);
  }
}

/**
 * Escape HTML special characters
 * @param {string} html
 * @param {boolean} includeQuotes
 * @return {string}
 */
function escapeHtml(html, includeQuotes = true) {
  if (!html) return html;
  html = html.toString()
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  if (includeQuotes)
    html = html
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  return html;
}

module.exports = {
  HOUR,
  DAY,
  YEAR,
  TRIAL_RESET_INTERVAL,
  OPEN_DATA_API_URL,
  OPEN_DATA_FIELDS,
  OPEN_DATA_DATASETS,
  COMMON_DOMAINS,
  getDomain,
  capitalizeName,
  normalizeName,
  normalizeCity,
  sorter,
  cleanUpSchoolObject,
  removeAccents,
  wordToPercentage,
  withTransaction,
  capitalizeFirstLetter,
  sourceToLetter,
  escapeHtml
};